import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Apollo, gql } from 'apollo-angular';
import { GraphQL } from '../../graphql/zip-utils.graphql';
import { GraphQLRedirectionType } from '../../enums/common';
import {
  IHealthCheckResponse,
  IGenerateZipTextUrlResponse,
  IGetZipTextUrlResponse,
  IGetZipShortUrlResponse,
  IGenerateZipShortUrlResponse,
  ICheckShortIdAvailabilityResponse,
  IDeleteZipTextResponse,
  IDeleteZipShortUrlResponse,
} from '../../models/common';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private readonly apollo = inject(Apollo);
  private readonly platformId = inject(PLATFORM_ID);
  private tempText: string = '';
  private tempIsOneTimeView: boolean = false;
  private tempHasPin: boolean = false;
  private tempIsIpRestricted: boolean = false;
  private tempExpiryInMinutes: number | null = null;
  private tempExpiryTime: number | null = null;
  private isFromBackend: boolean = false;
  private isUrlRedirect: boolean = false;

  setTempText(text: string) {
    this.tempText = text;
  }

  getTempText(): string {
    return this.tempText;
  }

  setTempIsOneTimeView(isOneTimeView: boolean) {
    this.tempIsOneTimeView = isOneTimeView;
  }

  getTempIsOneTimeView(): boolean {
    return this.tempIsOneTimeView;
  }

  setTempHasPin(hasPin: boolean) {
    this.tempHasPin = hasPin;
  }

  getTempHasPin(): boolean {
    return this.tempHasPin;
  }

  setTempIsIpRestricted(isIpRestricted: boolean) {
    this.tempIsIpRestricted = isIpRestricted;
  }

  getTempIsIpRestricted(): boolean {
    return this.tempIsIpRestricted;
  }

  setTempExpiryInMinutes(expiryInMinutes: number | null) {
    this.tempExpiryInMinutes = expiryInMinutes;
  }

  getTempExpiryInMinutes(): number | null {
    return this.tempExpiryInMinutes;
  }

  setTempExpiryTime(expiryTime: number | null) {
    this.tempExpiryTime = expiryTime;
  }

  getTempExpiryTime(): number | null {
    return this.tempExpiryTime;
  }

  setIsFromBackend(value: boolean) {
    this.isFromBackend = value;
  }

  getIsFromBackend(): boolean {
    return this.isFromBackend;
  }

  clearTempText() {
    this.tempText = '';
    this.tempIsOneTimeView = false;
    this.tempHasPin = false;
    this.tempIsIpRestricted = false;
    this.tempExpiryInMinutes = null;
    this.tempExpiryTime = null;
    this.isFromBackend = false;
  }

  setIsUrlRedirect(value: boolean) {
    this.isUrlRedirect = value;
  }

  getIsUrlRedirect(): boolean {
    return this.isUrlRedirect;
  }

  healthCheck() {
    return this.apollo.query<IHealthCheckResponse>({
      query: GraphQL.HealthCheck,
      fetchPolicy: 'no-cache',
    });
  }

  getZipText(id: string, pin?: string | null) {
    return this.apollo.query<IGetZipTextUrlResponse>({
      query: GraphQL.getZipText,
      variables: { url: id, pin },
      fetchPolicy: 'no-cache',
    });
  }

  generateZipTextUrl(
    text: string,
    expiryInMinutes: number | null,
    customSlug?: string | null,
    isIpRestricted?: boolean,
    isOneTimeView?: boolean,
    pin?: string | null,
  ) {
    const MUTATION = GraphQL.generateZipTextUrl;

    return this.apollo.mutate<IGenerateZipTextUrlResponse>({
      mutation: MUTATION,
      variables: {
        text,
        expiryInMinutes,
        customSlug,
        isIpRestricted,
        isOneTimeView,
        pin,
      },
    });
  }

  generateZipShortUrl(
    url: string,
    expiryInMinutes: number | null,
    customSlug?: string | null,
    isOneTimeView?: boolean,
    pin?: string | null,
  ) {
    return this.apollo.mutate<IGenerateZipShortUrlResponse>({
      mutation: GraphQL.generateZipShortUrl,
      variables: { url, expiryInMinutes, customSlug, isOneTimeView, pin },
    });
  }

  getZipShortUrl(id: String, pin?: string | null) {
    return this.apollo.query<IGetZipShortUrlResponse>({
      query: GraphQL.getZipShortUrl,
      variables: { url: id, pin },
      fetchPolicy: 'no-cache',
    });
  }

  deleteZipShortUrl(id: string) {
    return this.apollo.mutate<IDeleteZipShortUrlResponse>({
      mutation: GraphQL.deleteZipShortUrl,
      variables: { id },
    });
  }

  /**
   * 🔍 Check if custom short ID is available
   */
  checkShortIdAvailability(id: string, type: GraphQLRedirectionType) {
    return this.apollo.query<ICheckShortIdAvailabilityResponse>({
      query: GraphQL.isShortIdAvailable,
      variables: { id, type },
      fetchPolicy: 'no-cache',
    });
  }

  deleteZipText(id: string) {
    return this.apollo.mutate<IDeleteZipTextResponse>({
      mutation: GraphQL.deleteZipText,
      variables: { id },
    });
  }

  downloadQr(canvasSelector: string, filename: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const canvas = document.querySelector(canvasSelector) as HTMLCanvasElement;
    if (!canvas) return;

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${filename}.png`;
    link.click();
  }
}
